// Based on https://en.wikipedia.org/wiki/Centripetal_Catmull%E2%80%93Rom_spline#Code_example_in_Unity_C#

import { vec3 } from "gl-matrix";
import { lerp } from "./interpolation";

function knotInterval(p0: vec3, p1: vec3, alpha: number = 0.5) {
  const squaredMagnitude = vec3.squaredDistance(p0, p1);
  return Math.pow(squaredMagnitude, 0.5 * alpha);
}

function remap(a: number, b: number, c: vec3, d: vec3, u: number): vec3 {
  const thing1 = (u - a) / (b - a);

  return [
    lerp(c[0], d[0], thing1),
    lerp(c[1], d[1], thing1),
    lerp(c[2], d[2], thing1),
  ];
}

export function catmullRomCurve(
  p0: vec3,
  p1: vec3,
  p2: vec3,
  p3: vec3,
  alpha: number = 0.5,
): (t: number) => vec3 {
  // calculate knots
  const k0 = 0;
  const k1 = knotInterval(p0, p1, alpha) + k0;
  const k2 = knotInterval(p1, p2, alpha) + k1;
  const k3 = knotInterval(p2, p3, alpha) + k2;

  // evaluate point at t
  return (t: number) => {
    const u = lerp(k1, k2, t);
    const A1 = remap(k0, k1, p0, p1, u);
    const A2 = remap(k1, k2, p1, p2, u);
    const A3 = remap(k2, k3, p2, p3, u);
    const B1 = remap(k0, k2, A1, A2, u);
    const B2 = remap(k1, k3, A2, A3, u);

    return remap(k1, k2, B1, B2, u);
  };
}
